import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import api from '../services/api';
import type { DashboardStats, RecentActivity } from '../types';
import { format } from 'date-fns';

// Stat card component
function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  linkTo,
  borderColor = 'border-l-blue-500',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: { value: number; label: string };
  linkTo?: string;
  borderColor?: string;
}) {
  const content = (
    <Card className={`hover:shadow-md transition-shadow border-l-4 ${borderColor}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <p className={`text-sm mt-2 ${trend.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
            </p>
          )}
        </div>
        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
          {icon}
        </div>
      </div>
    </Card>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}

// Activity item component
function ActivityItem({ activity }: { activity: RecentActivity }) {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'order_submitted':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'order_shipped':
        return (
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </div>
        );
      case 'production_complete':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'inventory_low':
        return (
          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="flex items-start gap-3 py-3">
      {getActivityIcon()}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">{activity.message}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {format(new Date(activity.timestamp), 'MMM d, h:mm a')}
        </p>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    async function loadDashboard() {
      try {
        const [statsData, activityData] = await Promise.all([
          api.getDashboardStats().catch(() => null),
          api.getRecentActivity(10).catch(() => []),
        ]);
        // Map API response to expected format
        if (statsData) {
          setStats({
            orders: {
              pending: statsData.orders?.pending || 0,
              inProduction: statsData.orders?.inProduction || 0,
              readyToShip: statsData.orders?.readyToShip || 0,
              shippedThisWeek: statsData.orders?.shippedThisWeek || 0,
              pendingPayment: statsData.orders?.pendingPayment || 0,
              pendingPaymentTotal: statsData.orders?.pendingPaymentTotal || 0,
              totalScreenUnits: statsData.orders?.totalScreenUnits || 0,
              totalOrders: statsData.orders?.totalOrders || 0,
            },
            production: {
              scheduledToday: statsData.production?.scheduledToday || 0,
              completedToday: statsData.production?.completedToday || 0,
              blockedItems: statsData.production?.blockedItems || 0,
              averageLeadTime: statsData.production?.averageLeadTime || 0,
            },
            inventory: {
              lowStockItems: statsData.inventory?.lowStockItems || 0,
              pendingReceiving: statsData.inventory?.pendingReceiving || 0,
              criticalItems: statsData.inventory?.criticalItems || [],
            },
            revenue: {
              thisMonth: statsData.revenue?.thisMonth || 0,
              lastMonth: statsData.revenue?.lastMonth || 0,
              thisYear: statsData.revenue?.thisYear || 0,
            },
            receivables: statsData.receivables || {
              outstandingBalance: 0, outstandingCount: 0,
              partialPaymentBalance: 0, partialPaymentCount: 0,
              pendingAchTotal: 0, pendingAchCount: 0,
              qbAdjustmentCount: 0,
            },
            credits: statsData.credits || {
              availableCreditTotal: 0, creditDealerCount: 0,
              openOverpaymentTotal: 0, openOverpaymentCount: 0,
            },
            screens: statsData.screens || undefined,
          });
        } else {
          // Show zeros if API fails
          setStats({
            orders: { pending: 0, inProduction: 0, readyToShip: 0, shippedThisWeek: 0, pendingPayment: 0, pendingPaymentTotal: 0 },
            production: { scheduledToday: 0, completedToday: 0, blockedItems: 0, averageLeadTime: 0 },
            inventory: { lowStockItems: 0, pendingReceiving: 0, criticalItems: [] },
            revenue: { thisMonth: 0, lastMonth: 0, thisYear: 0 },
          });
        }
        setActivities(activityData || []);
      } catch (error) {
        console.error('Failed to load dashboard:', error);
        // Show zeros if API fails
        setStats({
          orders: { pending: 0, inProduction: 0, readyToShip: 0, shippedThisWeek: 0 },
          production: { scheduledToday: 0, completedToday: 0, blockedItems: 0, averageLeadTime: 0 },
          inventory: { lowStockItems: 0, pendingReceiving: 0 },
          revenue: { thisMonth: 0, lastMonth: 0, thisYear: 0 },
        });
        setActivities([]);
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!stats) {
    return <div>Failed to load dashboard</div>;
  }

  return (
    <div>
      <PageHeader 
        title="Dashboard" 
        subtitle={`Welcome back! Here's what's happening today.`}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Pending Orders"
          value={stats.orders.pending}
          subtitle="Awaiting release"
          borderColor="border-l-blue-500"
          linkTo="/orders?status=pending_release"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="In Production"
          value={stats.orders.inProduction}
          subtitle={`${stats.production.blockedItems} blocked`}
          borderColor="border-l-yellow-500"
          linkTo="/production"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          }
        />
        <StatCard
          title="Ready to Ship"
          value={stats.orders.readyToShip}
          subtitle="Complete orders"
          borderColor="border-l-green-500"
          linkTo="/orders?status=ready"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          }
        />
        <StatCard
          title="Shipped This Week"
          value={stats.orders.shippedThisWeek}
          subtitle="Orders delivered"
          borderColor="border-l-purple-500"
          linkTo="/orders?status=shipped"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Screen Units Sold"
          value={stats.orders.totalScreenUnits || 0}
          subtitle={`${stats.orders.totalOrders || 0} orders — all time`}
          borderColor="border-l-indigo-500"
          linkTo="/orders"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          }
        />
      </div>

      {/* Screen-Unit Funnel — built/in-flight/queued (drafts shown separately
          since they're floating quotes, not real build commitments) */}
      {stats.screens && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Screens Completed"
            value={stats.screens.completed}
            subtitle="All-time, built and finished"
            borderColor="border-l-emerald-500"
            linkTo="/orders?status=ready,shipped,delivered"
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Screens In Production"
            value={stats.screens.inProduction}
            subtitle="On the floor right now"
            borderColor="border-l-yellow-500"
            linkTo="/production"
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
          <StatCard
            title="Awaiting Production"
            value={stats.screens.awaitingProduction}
            subtitle="Submitted, not yet started"
            borderColor="border-l-orange-500"
            linkTo="/orders?status=submitted,pending_release,pending_payment"
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Screens in Drafts"
            value={stats.screens.inDrafts}
            subtitle="Floating quotes (not committed)"
            borderColor="border-l-gray-400"
            linkTo="/orders?status=draft"
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Production Today */}
        <Card className="border-l-4 border-l-blue-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Production Today</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Scheduled</span>
              <span className="text-2xl font-bold text-gray-900">{stats.production.scheduledToday}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Completed</span>
              <span className="text-2xl font-bold text-green-600">{stats.production.completedToday}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{ width: `${stats.production.scheduledToday > 0 ? (stats.production.completedToday / stats.production.scheduledToday) * 100 : 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-500">
              {stats.production.scheduledToday > 0 ? Math.round((stats.production.completedToday / stats.production.scheduledToday) * 100) : 0}% complete
            </p>
          </div>
        </Card>

        {/* Revenue */}
        <Card className="border-l-4 border-l-green-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                ${stats.revenue.thisMonth.toLocaleString()}
              </p>
              {stats.revenue.lastMonth > 0 && (
                <p className={`text-sm ${stats.revenue.thisMonth >= stats.revenue.lastMonth ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.revenue.thisMonth >= stats.revenue.lastMonth ? '↑' : '↓'}{' '}
                  {Math.abs(Math.round(((stats.revenue.thisMonth - stats.revenue.lastMonth) / stats.revenue.lastMonth) * 100))}% vs last month
                </p>
              )}
            </div>
            {stats.revenue.lastMonth > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">Last Month</p>
                <p className="text-xl font-semibold text-gray-700">
                  ${stats.revenue.lastMonth.toLocaleString()}
                </p>
              </div>
            )}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">Year to Date</p>
              <p className="text-xl font-semibold text-gray-900">
                ${stats.revenue.thisYear.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        {/* Accounts Receivable */}
        {stats.receivables && (
          <Card className="border-l-4 border-l-orange-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Accounts Receivable</h3>
            <div className="space-y-3">
              <Link to="/order-overview?status=shipped,delivered,completed&paid=outstanding" className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-red-800">Outstanding Balance</p>
                  <p className="text-xs text-red-600">{stats.receivables.outstandingCount} orders unpaid</p>
                </div>
                <span className="text-xl font-bold text-red-600">
                  ${stats.receivables.outstandingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
              <Link to="/order-overview?paid=partial" className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-amber-800">Partial Payments</p>
                  <p className="text-xs text-amber-600">{stats.receivables.partialPaymentCount} orders with balance remaining</p>
                </div>
                <span className="text-xl font-bold text-amber-600">
                  ${stats.receivables.partialPaymentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
              <Link to="/order-overview?pendingAch=true" className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-blue-800">ACH Pending Deposit</p>
                  <p className="text-xs text-blue-600">{stats.receivables.pendingAchCount} ACH awaiting bank deposit</p>
                </div>
                <span className="text-xl font-bold text-blue-600">
                  ${stats.receivables.pendingAchTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
              <Link to="/order-overview?status=submitted,pending_payment" className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-gray-800">Submitted — No Payment</p>
                  <p className="text-xs text-gray-600">{stats.receivables.submittedUnpaidCount || 0} orders awaiting payment</p>
                </div>
                <span className="text-xl font-bold text-gray-600">
                  ${(stats.receivables.submittedUnpaidTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
              {stats.receivables.qbAdjustmentCount > 0 && (
                <Link to="/order-overview?qbAdjustment=true" className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-orange-800">QB Adjustments Needed</p>
                    <p className="text-xs text-orange-600">Invoice changed after QB sync</p>
                  </div>
                  <span className="text-xl font-bold text-orange-600 animate-pulse">
                    {stats.receivables.qbAdjustmentCount}
                  </span>
                </Link>
              )}
            </div>
          </Card>
        )}

        {/* Dealer Credits & Overpayments — money we owe dealers */}
        {stats.credits && (
          <Card className="border-l-4 border-l-emerald-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dealer Credits &amp; Overpayments</h3>
            <div className="space-y-3">
              <Link to="/accounting/credits" className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-emerald-800">Available Store Credit</p>
                  <p className="text-xs text-emerald-600">{stats.credits.creditDealerCount} dealers with a balance</p>
                </div>
                <span className="text-xl font-bold text-emerald-600">
                  ${stats.credits.availableCreditTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
              <Link to="/accounting/credits?tab=overpayments" className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <div>
                  <p className="text-sm font-medium text-purple-800">Open Overpayments</p>
                  <p className="text-xs text-purple-600">{stats.credits.openOverpaymentCount} orders paid over total</p>
                </div>
                <span className="text-xl font-bold text-purple-600">
                  ${stats.credits.openOverpaymentTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
            </div>
          </Card>
        )}

        {/* Inventory Alerts */}
        <Card className="border-l-4 border-l-yellow-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory</h3>
          <div className="space-y-3">
            <Link
              to="/inventory?lowStock=true"
              className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-yellow-800">Low Stock Items</span>
              </div>
              <span className="text-xl font-bold text-yellow-600">{stats.inventory.lowStockItems}</span>
            </Link>

            {/* Critical Stock Items */}
            {stats.inventory.criticalItems?.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {stats.inventory.criticalItems.slice(0, 3).map((item: any) => (
                  <Link
                    key={item.partNumber}
                    to={`/inventory?search=${item.partNumber}`}
                    className="flex items-center justify-between px-3 py-2 bg-red-50 rounded text-xs hover:bg-red-100 transition-colors"
                  >
                    <span className="text-red-800 font-medium truncate mr-2">
                      {item.partNumber} — {item.description?.substring(0, 30)}
                    </span>
                    <span className="text-red-600 font-bold whitespace-nowrap">
                      {item.onHand}{item.displayUnit ? ` ${item.displayUnit}` : ''} / {item.minQty}
                    </span>
                  </Link>
                ))}
              </div>
            )}

            {/* Invoice Correction Requests */}
            {(stats.orders as any).pendingCorrections > 0 && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-red-800">Invoice Corrections Needed</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">{(stats.orders as any).pendingCorrections}</span>
                </div>
                {(stats.orders as any).correctionOrders?.map((co: any) => (
                  <Link
                    key={co.id}
                    to={`/orders/${co.id}?tab=invoice`}
                    className="block px-3 py-2 mt-1.5 bg-white rounded text-xs hover:bg-red-100 transition-colors border border-red-100"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-red-800">{co.orderNumber}</span>
                      <span className="text-red-500">{co.dealerName}</span>
                    </div>
                    <p className="text-red-600 mt-0.5 truncate">{co.message}</p>
                  </Link>
                ))}
              </div>
            )}

          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-l-4 border-l-blue-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <Link to="/reports" className="text-sm text-blue-600 hover:text-blue-700">
            View all →
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {activities.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>No recent activity</p>
              <p className="text-sm mt-1">Activity will appear here as orders are processed</p>
            </div>
          ) : (
            activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))
          )}
        </div>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <Card className="border-l-4 border-l-purple-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
          <div className="space-y-3">
            <a
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-purple-900">Stripe Dashboard</p>
                <p className="text-sm text-purple-700">View payments and transactions</p>
              </div>
              <svg className="w-5 h-5 text-purple-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://qbo.intuit.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-900">QuickBooks Online</p>
                <p className="text-sm text-green-700">Manage accounting and invoices</p>
              </div>
              <svg className="w-5 h-5 text-green-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
          <div className="space-y-3">
            <button
              onClick={() => setShowSupportModal(true)}
              className="w-full flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-blue-900">Contact Support</p>
                <p className="text-sm text-blue-700">Get help with the system</p>
              </div>
            </button>
          </div>
        </Card>
      </div>

      {/* Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSupportModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-center">
              <div className="text-6xl mb-2">&#128683;</div>
              <h2 className="text-2xl font-bold text-white">IT Support Department</h2>
              <p className="text-red-100 text-sm mt-1">Official Response</p>
            </div>
            <div className="p-6 text-center">
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-4">
                <p className="text-lg font-bold text-gray-800 mb-2">Your support request has been reviewed.</p>
                <p className="text-gray-700">After careful consideration, the IT department (Toby) has determined that you should <strong>stop being lazy and figure it out yourself</strong>.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
                <p className="text-sm text-gray-600 font-medium mb-1">Alternatively, you may contact:</p>
                <p className="text-sm text-gray-800">&#128100; <strong>Toby</strong> — but only if you've already tried turning it off and on again</p>
                <p className="text-sm text-gray-800">&#128100; <strong>Justin</strong> — good luck with that</p>
              </div>
              <p className="text-xs text-gray-400 italic mb-4">Average response time: whenever we feel like it</p>
              <button
                onClick={() => setShowSupportModal(false)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                I'll Figure It Out Myself
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
